import { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { theme } from "../../theme";
import XS from "./typography/XS";
import { useTranslation } from "react-i18next";
import Tooltip from "../molecules/Tooltip";

interface Props {
  tagTitleKey: string;
  variant?:
    | "primary"
    | "warning"
    | "info"
    | "black"
    | "info20"
    | "secondary"
    | "pink"
    | "green";
  small?: boolean;
}

const Container = styled.div<{ $borderColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  padding: 2px 8px;
  border: 0.5px solid ${(props) => props.$borderColor};
  height: fit-content;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  background-color: ${theme.colors.white};
`;

const SmallTag = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`;

const TextTag: React.FC<Props> = ({
  tagTitleKey,
  variant = "primary",
  small,
}) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tagTitleKey]);

  if (!tagTitleKey) {
    return null;
  }

  const getTextColor = () => {
    switch (variant) {
      case "primary":
        return theme.colors.primary;
      case "warning":
        return theme.colors.warning;
      case "info":
        return theme.colors.info;
      case "black":
        return theme.colors.black;
      case "info20":
        return theme.colors.info20;
      case "secondary":
        return theme.colors.secondary50;
      case "pink":
        return theme.colors.pink;
      case "green":
        return theme.colors.green;
      default:
        return theme.colors.primary;
    }
  };
  const getBorderColor = () => {
    switch (variant) {
      case "primary":
        return theme.colors.primaryBg;
      case "warning":
        return theme.colors.warning;
      case "info":
        return theme.colors.info;
      case "black":
        return theme.colors.black;
      case "info20":
        return theme.colors.info20;
      case "secondary":
        return theme.colors.secondary50;
      case "pink":
        return theme.colors.pink;
      case "green":
        return theme.colors.green;
      default:
        return theme.colors.primary;
    }
  };
  if (small) {
    return (
      <Tooltip text={t(tagTitleKey)}>
        <SmallTag $color={getTextColor()} />
      </Tooltip>
    );
  }

  return (
    <Tooltip text={isTruncated ? t(tagTitleKey) : undefined}>
      <Container $borderColor={getBorderColor()}>
        <XS
          ref={textRef}
          color={getTextColor()}
          fontweight={
            variant === "primary"
              ? "normal"
              : variant === "black"
                ? "normal"
                : "bold"
          }
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {t(tagTitleKey)}
        </XS>
      </Container>
    </Tooltip>
  );
};

export default TextTag;
